import {
  isRouteErrorResponse,
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  useLocation,
} from 'react-router';
import { useEffect } from 'react';
import { Toaster } from 'sonner';
import { GtmScript } from './components/gtm-script';

import type { Route } from './+types/root';
import './app.css';
import { AuthProvider, useAuth } from './context/auth';
import { PurchaseProvider } from './context/purchase';
import { ActivitiesAccessProvider } from './context/activities-access';
import { NavigationProvider } from './context/navigation';
import Header from './components/header';
import LoadingScreen from './components/loading-screen';
import CartModal from './components/cart-modal';
import { isProtectedRoute } from './routes';
import { initSentry, Sentry } from './config/sentry';

initSentry();

const ROUTES_WITHOUT_HEADER = ['/cronograma/configurar'];

export const links: Route.LinksFunction = () => [
  { rel: 'icon', type: 'image/png', sizes: '16x16', href: '/favicon-16x16.png' },
  { rel: 'icon', type: 'image/png', sizes: '32x32', href: '/favicon-32x32.png' },
  { rel: 'preconnect', href: 'https://fonts.googleapis.com' },
  {
    rel: 'preconnect',
    href: 'https://fonts.gstatic.com',
    crossOrigin: 'anonymous',
  },
  {
    rel: 'stylesheet',
    href: 'https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap',
  },
];

export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body>
        <GtmScript />
        <Toaster richColors position="top-center" />
        <AuthProvider>
          <NavigationProvider>
            <ActivitiesAccessProvider>
              <PurchaseProvider>{children}</PurchaseProvider>
            </ActivitiesAccessProvider>
          </NavigationProvider>
        </AuthProvider>
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

function AppContent() {
  const { isLoading, isAuthenticated, login } = useAuth();
  const location = useLocation();

  const shouldHideHeader = ROUTES_WITHOUT_HEADER.some((route) =>
    location.pathname.startsWith(route),
  );

  useEffect(() => {
    if (!isLoading) {
      const isRouteProtected = isProtectedRoute(location.pathname);
      if (isRouteProtected && !isAuthenticated) {
        login();
      }
    }
  }, [isLoading, location.pathname, isAuthenticated, login]);

  useEffect(() => {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'virtualPageView',
      page_path: location.pathname + location.search,
    });
  }, [location.pathname, location.search]);

  const isRouteProtected = isProtectedRoute(location.pathname);
  const shouldBlockAccess = isRouteProtected && !isAuthenticated;

  if (isLoading || shouldBlockAccess) {
    return <LoadingScreen />;
  }

  if (shouldHideHeader) {
    return (
      <>
        <Outlet />
        <CartModal />
      </>
    );
  }

  return (
    <div className="h-screen flex flex-col bg-white">
      <Header />
      <main className="flex-1 overflow-y-auto">
        <Outlet />
      </main>
      <CartModal />
    </div>
  );
}

export default function App() {
  return <AppContent />;
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = 'Oops!';
  let details = 'An unexpected error occurred.';
  let stack: string | undefined;

  if (!isRouteErrorResponse(error)) {
    Sentry.captureException(error);
  }

  if (isRouteErrorResponse(error)) {
    message = error.status === 404 ? '404' : 'Error';
    details =
      error.status === 404 ? 'The requested page could not be found.' : error.statusText || details;
  } else if (import.meta.env.DEV && error && error instanceof Error) {
    details = error.message;
    stack = error.stack;
  }

  return (
    <main className="pt-8 max-w-7xl mx-auto px-4">
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre className="w-full p-4 overflow-x-auto">
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
